import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { SectionTitle, StatusPill } from "@/components/dashboard/widgets";
import RoutingExplainer from "@/components/dashboard/RoutingExplainer";
import { GitBranch, Plus, Loader2, Edit3, FileText, Trash2, Power, PowerOff, Activity, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminRouting() {
  const [trees, setTrees] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTree, setEditTree] = useState(null);
  const [logsTree, setLogsTree] = useState(null);
  const [logsData, setLogsData] = useState(null);

  const refresh = () => api.get("/admin/routing").then(r => setTrees(r.data?.trees || []));
  useEffect(() => {
    refresh();
    api.get("/catalog/sectors").then(r => setSectors(r.data || []));
  }, []);

  const toggleLive = async (t) => {
    await api.patch(`/admin/ping-trees/${t.id}`, { live: !t.live });
    toast.success(t.live ? "Tree paused" : "Tree resumed");
    refresh();
  };
  const deleteTree = async (t) => {
    if (!window.confirm(`Delete ping tree "${t.name}"? Routing will fall back to direct campaign matching.`)) return;
    await api.delete(`/admin/ping-trees/${t.id}`);
    toast.success("Deleted");
    refresh();
  };
  const openLogs = async (t) => {
    setLogsTree(t);
    setLogsData(null);
    try {
      const { data } = await api.get(`/admin/ping-trees/${t.id}/logs`);
      setLogsData(data);
    } catch (e) {
      toast.error("Could not load logs");
    }
  };

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Routing"
        subtitle="Optional multi-buyer auction layer. Sits in front of campaign-based routing."
      />

      <RoutingExplainer audience="admin" storageKey="routing-explainer-admin-routing" />

      <div className="rounded-md border border-emerald-200 bg-emerald-50/50 px-4 py-3 flex items-start gap-3" data-testid="ping-tree-status-notice">
        <AlertCircle className="size-4 text-emerald-700 shrink-0 mt-0.5" />
        <div className="text-[13px] text-emerald-900 leading-snug">
          <span className="font-semibold">Ping trees are now live — CPL waterfall routing.</span> When a tree exists for a sector with assigned buyer nodes, inbound leads are routed to the highest-priority eligible buyer first, cascading down the tree on any rejection (paused / outside-hours / cap-reached / empty wallet). Same flat sector CPL applies — buyers don't bid against each other, the tree decides <em>who gets this lead first</em>.
        </div>
      </div>

      <div className="flex items-center justify-end">
        <Button onClick={() => setCreateOpen(true)} className="rounded-full bg-[#0F172A] hover:bg-slate-800 gap-2" data-testid="routing-new-tree">
          <Plus className="size-4" /> New ping tree
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {trees.length === 0 ? (
          <Card className="lg:col-span-2 rounded-xl border-dashed border-slate-200">
            <CardContent className="p-10 text-center text-slate-500">
              <GitBranch className="size-8 mx-auto mb-3 text-slate-300" />
              <div className="font-medium text-slate-700">No ping trees yet</div>
              <div className="text-sm mt-1">Create one to start auctioning leads across multiple buyers in parallel.</div>
            </CardContent>
          </Card>
        ) : trees.map(t => (
          <Card key={t.id} className="rounded-xl shadow-soft border-slate-200" data-testid={`routing-tree-${t.id.slice(0, 8)}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-slate-900 truncate">{t.name}</h3>
                    <StatusPill status={t.live ? "live" : "paused"} />
                  </div>
                  <div className="text-[12.5px] text-slate-500 mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                    <span>Sector: <span className="text-slate-700 font-medium capitalize">{t.sector}</span></span>
                    <span>{t.nodes?.length ?? 0} buyer nodes</span>
                    <span>£{t.min_price?.toFixed(2)} – £{t.max_price?.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="sm" variant="outline" className="rounded-full h-8 px-2.5 text-[11.5px]" onClick={() => toggleLive(t)} title={t.live ? "Pause" : "Resume"} data-testid={`routing-tree-toggle-${t.id.slice(0, 8)}`}>
                    {t.live ? <PowerOff className="size-3.5" /> : <Power className="size-3.5" />}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full h-8 px-2.5 text-[11.5px]" onClick={() => deleteTree(t)} title="Delete" data-testid={`routing-tree-delete-${t.id.slice(0, 8)}`}>
                    <Trash2 className="size-3.5 text-rose-600" />
                  </Button>
                </div>
              </div>

              {/* Buyer node mini-grid — clean and non-overlapping */}
              {t.nodes?.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {t.nodes.slice(0, 6).map((n, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 min-w-0">
                      <div className="text-[12px] font-medium text-slate-900 truncate">{n.buyer_name || `Buyer ${i + 1}`}</div>
                      <div className="text-[10.5px] text-slate-500">Weight {n.weight ?? 1} · Bid £{(n.target_bid || 0).toFixed(0)}</div>
                    </div>
                  ))}
                  {t.nodes.length > 6 && (
                    <div className="rounded-lg border border-dashed border-slate-200 px-2.5 py-1.5 text-center text-[12px] text-slate-500">
                      +{t.nodes.length - 6} more
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex items-center justify-between text-[12.5px] text-slate-500">
                <span>Created {new Date(t.created_at).toLocaleDateString("en-GB")}</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="rounded-full h-7 px-3" onClick={() => openLogs(t)} data-testid={`routing-tree-logs-${t.id.slice(0, 8)}`}>
                    <FileText className="size-3" /> View logs
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full h-7 px-3" onClick={() => setEditTree(t)} data-testid={`routing-tree-edit-${t.id.slice(0, 8)}`}>
                    <Edit3 className="size-3" /> Edit
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <PingTreeFormDialog open={createOpen || !!editTree} onClose={() => { setCreateOpen(false); setEditTree(null); }} onSaved={refresh} existing={editTree} sectors={sectors} />
      <PingTreeLogsSheet open={!!logsTree} onClose={() => { setLogsTree(null); setLogsData(null); }} tree={logsTree} data={logsData} />
    </div>
  );
}

function PingTreeFormDialog({ open, onClose, onSaved, existing, sectors }) {
  const isEdit = !!existing;
  const [name, setName] = useState("");
  const [sector, setSector] = useState("mortgages");
  const [minPrice, setMinPrice] = useState(30);
  const [maxPrice, setMaxPrice] = useState(120);
  const [nodes, setNodes] = useState([]);
  const [live, setLive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/admin/users")
        .then(r => setClients((r.data || []).filter(u => (u.role === "client" || u.role === "admin") && u.id).map(u => ({
          id: u.id,
          label: `${u.name || u.email} ${u.company ? `· ${u.company}` : ""}`,
        }))))
        .catch(() => setClients([]));
    }
  }, [open]);

  useEffect(() => {
    if (existing) {
      setName(existing.name || "");
      setSector(existing.sector || "mortgages");
      setMinPrice(existing.min_price ?? 30);
      setMaxPrice(existing.max_price ?? 120);
      setNodes(existing.nodes || []);
      setLive(existing.live !== false);
    } else {
      setName("");
      setSector("mortgages");
      setMinPrice(30);
      setMaxPrice(120);
      setNodes([{ buyer_name: "Buyer A", weight: 1, target_bid: 45 }]);
      setLive(true);
    }
  }, [existing, open]);

  const addNode = () => setNodes([...nodes, { buyer_name: `Buyer ${String.fromCharCode(65 + nodes.length)}`, weight: 1, target_bid: 45 }]);
  const removeNode = (i) => setNodes(nodes.filter((_, idx) => idx !== i));
  const updateNode = (i, k, v) => setNodes(nodes.map((n, idx) => idx === i ? { ...n, [k]: v } : n));

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (nodes.length === 0) { toast.error("Add at least one buyer node"); return; }
    const payload = {
      name: name.trim(),
      sector,
      min_price: parseFloat(minPrice) || 0,
      max_price: parseFloat(maxPrice) || 0,
      live,
      nodes: nodes.map(n => ({
        buyer_name: (n.buyer_name || "").trim() || "Buyer",
        client_id: n.client_id || null,
        weight: parseFloat(n.weight) || 1,
        target_bid: parseFloat(n.target_bid) || 0,
      })),
    };
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/ping-trees/${existing.id}`, payload);
        toast.success("Ping tree updated");
      } else {
        await api.post("/admin/ping-trees", payload);
        toast.success("Ping tree created");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="pingtree-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit ping tree" : "New ping tree"}</DialogTitle>
          <DialogDescription>Set the sector, price band, and buyer nodes. Changes apply on the next inbound lead.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label>Tree name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="UK Mortgages — Tier 1" className="mt-1 h-10" data-testid="pingtree-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sector</Label>
              <Select value={sector} onValueChange={setSector}>
                <SelectTrigger data-testid="pingtree-sector" className="mt-1 h-10"><SelectValue placeholder="Sector" /></SelectTrigger>
                <SelectContent>{sectors.map(s => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/60 px-3">
              <div>
                <div className="text-[13px] font-medium text-slate-900">Live</div>
                <div className="text-[11.5px] text-slate-500">Toggle off to pause this tree</div>
              </div>
              <Switch checked={live} onCheckedChange={setLive} data-testid="pingtree-live-toggle" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Min bid £</Label>
              <Input type="number" min={0} step="0.01" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="mt-1 h-10 tabular-nums" />
            </div>
            <div>
              <Label>Max bid £</Label>
              <Input type="number" min={0} step="0.01" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="mt-1 h-10 tabular-nums" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Buyer nodes</Label>
              <Button size="sm" variant="outline" className="rounded-full h-7" onClick={addNode}><Plus className="size-3" /> Add node</Button>
            </div>
            <p className="text-[11.5px] text-slate-500 mb-2 leading-snug">
              Higher weight = higher priority in the waterfall. Pick a real buyer to make this node routable; unassigned nodes are skipped at runtime.
            </p>
            <div className="space-y-2">
              {nodes.map((n, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-2.5 space-y-2">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-8">
                      <Select value={n.client_id || ""} onValueChange={(v) => {
                        const found = clients.find(c => c.id === v);
                        updateNode(i, "client_id", v || null);
                        if (found && (!n.buyer_name || n.buyer_name.startsWith("Buyer "))) {
                          updateNode(i, "buyer_name", found.label);
                        }
                      }}>
                        <SelectTrigger className="h-9 text-[13px]" data-testid={`pingtree-node-${i}-client`}>
                          <SelectValue placeholder="Pick a buyer (or leave unassigned)" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">No clients loaded</div>}
                          {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input type="number" min={0.1} step="0.1" value={n.weight} onChange={(e) => updateNode(i, "weight", e.target.value)} placeholder="Weight" className="col-span-3 h-9 text-[13px] tabular-nums" data-testid={`pingtree-node-${i}-weight`} />
                    <Button size="sm" variant="ghost" className="col-span-1 h-9 px-2" onClick={() => removeNode(i)}><Trash2 className="size-3.5 text-rose-600" /></Button>
                  </div>
                  <Input value={n.buyer_name} onChange={(e) => updateNode(i, "buyer_name", e.target.value)} placeholder="Display name (optional)" className="h-8 text-[12px]" data-testid={`pingtree-node-${i}-name`} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="pingtree-save">
            {busy ? <><Loader2 className="size-4 animate-spin" /> Saving…</> : (isEdit ? "Save changes" : "Create ping tree")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PingTreeLogsSheet({ open, onClose, tree, data }) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="pingtree-logs-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Activity className="size-5 text-[#0055FF]" /> {tree?.name || ""} — logs</SheetTitle>
          <SheetDescription>Most recent leads routed through this tree (last 50 in this sector).</SheetDescription>
        </SheetHeader>
        {!data ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="size-5 animate-spin mx-auto mb-2" /> Loading…</div>
        ) : (
          <div className="mt-4 space-y-2">
            {data.logs?.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm italic">No leads in this sector yet.</div>
            ) : data.logs.map(l => (
              <div key={l.id} className="rounded-lg border border-slate-200 bg-white p-3 text-[13px] flex items-center justify-between gap-3" data-testid={`pingtree-log-${l.id.slice(0,8)}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{l.first_name} {l.last_name}</div>
                  <div className="text-[11.5px] text-slate-500">{l.publisher_email || "—"} · {new Date(l.created_at).toLocaleString("en-GB")}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-mono tabular-nums text-slate-900">£{Number(l.price || 0).toFixed(2)}</div>
                  <StatusPill status={l.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
